import { useState, useCallback, useRef, useEffect } from 'react';
import useSWR from 'swr';
import qs from 'query-string';
import cloneDeep from 'lodash.clonedeep';
import isEqual from 'lodash.isequal';

const APP_HOSTNAME = window.location.hostname;
const domain = APP_HOSTNAME === 'localhost' ? '' : `domain=.${APP_HOSTNAME};`;
const setCookie = (name, value, options) => {
  const expires = options && options.expires || options && options['max-age'] && getExpires(options['max-age']) || getExpires(60 * 30);
  const encodedValue = encodeURIComponent(value);
  const isTest = process.env.NODE_ENV === 'test';
  document.cookie = `${name}=${encodedValue}; expires=${expires};${isTest ? '' : ` secure=true; ${domain}`} path=/`;
  return value;
};
const getCookie = name => {
  const value = '; ' + document.cookie;
  const parts = value.split('; ' + name + '=');
  return parts.length >= 2 ? decodeURIComponent(parts[1].split(';').shift()) : undefined;
};
const deleteCookie = name => {
  return setCookie(name, '', {
    'max-age': -1
  });
};

function getExpires(maxAge) {
  // convert a maxAge to an 'expires' date.
  return new Date(Date.now() + maxAge * 1000).toUTCString();
}

var stashDefaults = {
  accessToken: 'stashAccessToken',
  refreshToken: 'stashRefreshToken',
  uuid: 'stashUUID'
};

const createAuthHandler = () => {
  // attempt to load values from cookies into memory on app start
  const data = {
    accessToken: getCookie(stashDefaults.accessToken),
    refreshToken: getCookie(stashDefaults.refreshToken),
    uuid: getCookie(stashDefaults.uuid)
  };

  const get = () => {
    // read values from cookies to keep in sync with main web repo
    return {
      accessToken: getCookie(stashDefaults.accessToken),
      refreshToken: getCookie(stashDefaults.refreshToken),
      uuid: getCookie(stashDefaults.uuid)
    };
  };

  const set = newData => {
    Object.keys(newData).forEach(key => {
      setCookie(stashDefaults[key], newData[key]);
      data[key] = newData[key];
    });
  };

  const deleteAll = () => {
    Object.keys(data).forEach(key => {
      delete data[key];
      deleteCookie(stashDefaults[key]);
    });
  };

  const logOut = reason => {
    deleteAll();
    window.localStorage && window.localStorage.clear();
    window.sessionStorage && window.sessionStorage.clear(); // Do a hard redirect on window to clear memory.

    window.setTimeout(() => {
      window.location.href = `/log-in?reason=${reason || ''}`;
    }, 300);
  };

  return {
    get,
    set,
    deleteAll,
    logOut
  };
};

var Auth = createAuthHandler();

function _catch(body, recover) {
  try {
    var result = body();
  } catch (e) {
    return recover(e);
  }

  if (result && result.then) {
    return result.then(void 0, recover);
  }

  return result;
}

// Helper function to check if a value looks like a Response object
const isResponseLike = value => {
  if (value === null || typeof value !== 'object') {
    return false;
  }

  const obj = value;
  return 'ok' in obj || 'status' in obj || 'json' in obj;
};

const unmockFetch = () => {
  // eslint-disable-next-line
  // @ts-ignore
  if (global.fetch && global.fetch.mockRestore) global.fetch.mockRestore();
};
/**
 * Enhanced mockFetch utility that supports status codes via Error objects
 *
 * Legacy usage (backward compatible):
 * mockFetch(async () => ({ foo: 'bar' }))  // Returns { ok: true, status: 200 }
 *
 * Status codes via Error objects (recommended):
 * mockFetch(async () => {
 *   const error = new Error('Unauthorized');
 *   error.status = 401;
 *   error.data = { error: 'Unauthorized' };  // Optional: custom response data
 *   throw error;
 * })
 *
 * The error-based approach is simple and doesn't require helper functions.
 */

const mockFetch = onFetch => {
  unmockFetch();

  const mockFetch = function (url, config) {
    try {
      let parsedBody;

      try {
        parsedBody = config.body ? JSON.parse(config.body) : undefined;
      } catch (e) {// Ignore JSON parse errors
      }

      return Promise.resolve(_catch(function () {
        return Promise.resolve(onFetch({ ...new Request(url, config),
          // a slightly hacky convenience
          // to offer access to the body object, if it exists
          body: parsedBody
        })).then(function (result) {
          return isResponseLike(result) ? {
            ok: result.ok !== undefined ? result.ok : (result.status || 200) < 400,
            status: result.status || (result.ok === false ? 400 : 200),
            ...result,
            json: result.json || function () {
              try {
                return Promise.resolve(result.data || result);
              } catch (e) {
                return Promise.reject(e);
              }
            }
          } : Promise.resolve({
            ok: true,
            status: 200,

            json() {
              return result;
            }

          });
        }); // Check if the result is already a Response-like object
      }, function (e) {
        // Check if the error has a status property
        if (e instanceof Error && 'status' in e) {
          const error = e;
          return Promise.resolve({
            ok: (error.status || 500) < 400,
            status: error.status || 500,
            json: function () {
              try {
                return Promise.resolve(error.message ? {
                  error: error.message
                } : error.data || {
                  error: error.message
                });
              } catch (e) {
                return Promise.reject(e);
              }
            }
          });
        } // Check if the error is already a Response-like object


        return isResponseLike(e) ? Promise.resolve({
          ok: e.ok !== undefined ? e.ok : false,
          status: e.status || 400,
          ...e,
          json: e.json || function () {
            try {
              return Promise.resolve(e.data || e);
            } catch (e) {
              return Promise.reject(e);
            }
          }
        }) : Promise.resolve({
          ok: false,
          status: 500,

          json() {
            return e;
          }

        });
      }));
    } catch (e) {
      return Promise.reject(e);
    }
  };

  jest.spyOn(global, 'fetch').mockImplementation(mockFetch);
};

var httpMethods = {
  POST: "POST",
  PUT: "PUT",
  PATCH: "PATCH",
  GET: "GET",
  DELETE: "DELETE",
  HEAD: "HEAD",
  OPTIONS: "OPTIONS"
};

function postmanMockFilter (url, fetchConfig) {
  const postmanUrl = url.replace(process.env.STASH_API_PATH || '', process.env.POSTMAN_MOCK_ENDPOINT || '');
  fetchConfig.headers['x-api-key'] = process.env.POSTMAN_MOCK_API_KEY;
  return [postmanUrl, fetchConfig];
}

const handleSessionChange = function (json) {
  try {
    if (json.api_key) Auth.set({
      accessToken: json.api_key.access_token,
      uuid: json.api_key.uuid,
      userId: json.api_key.user_id
    });
    return Promise.resolve();
  } catch (e) {
    return Promise.reject(e);
  }
};
const getJSON = function (res) {
  try {
    if (res.status === 204) return Promise.resolve('');
    return Promise.resolve(res.json()).then(function (json) {
      return res.ok ? Promise.resolve(json) : Promise.reject(json);
    });
  } catch (e) {
    return Promise.reject(e);
  }
};
const sendRequest = function (url, fetchArgs) {
  try {
    const fetchConfig = buildFetchConfig(fetchArgs);

    if (process.env.NODE_ENV !== 'production') {
      if (fetchArgs.postman) return Promise.resolve(fetch(...postmanMockFilter(url, fetchConfig)));
    }

    return Promise.resolve(fetch(url, fetchConfig));
  } catch (e) {
    return Promise.reject(e);
  }
};
function buildURL({
  root,
  path = '',
  params
} = {}, {
  uuid,
  accessToken
}) {
  const hasParams = params && Object.keys(params).length;
  const encodedParams = hasParams ? `?${qs.stringify(params, {
    skipNull: true,
    skipEmptyString: true
  })}` : '';
  const fallbackUuid = uuid ? uuid : getUuidFromToken(accessToken); // log out if uuid is missing

  if (!fallbackUuid) {
    Auth.logOut('missing user id');
  }

  if (!getCookie(stashDefaults.uuid)) {
    setCookie(stashDefaults.uuid, fallbackUuid);
  }

  return `${root}${path}${encodedParams}`.replace(':user_id', fallbackUuid || '').replace(':uuid', fallbackUuid || '');
}
function buildFetchConfig({
  method = httpMethods.GET,
  headers = {},
  body,
  accessToken,
  mode = 'cors',
  ...rest
}) {
  const authToken = accessToken || Auth.get().accessToken;
  const authHeader = !!authToken && !checkExpired(authToken) ? {
    Authorization: authToken
  } : {};
  return {
    headers: {
      'Content-Type': 'application/json',
      ...authHeader,
      ...headers
    },
    mode: mode || 'cors',
    body: body && JSON.stringify(body),
    method,
    ...rest
  };
}

function parseJwt(token) {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (e) {
    return null;
  }
}

function getExpiry(accessToken) {
  const parsed = parseJwt(accessToken.replace('Bearer ', ''));
  const {
    exp
  } = parsed; // multiply by 1000 to account for difference between Rails and JS dates

  return exp * 1000;
}

function checkExpired(accessToken) {
  if (!accessToken) {
    return false;
  }

  const expiry = getExpiry(accessToken);
  return new Date().getTime() >= expiry - 2000;
}
function getUuidFromToken(accessToken) {
  if (!accessToken) {
    return null;
  }

  const parsed = parseJwt(accessToken.replace('Bearer ', ''));

  if (parsed) {
    return parsed.sub;
  } else {
    return null;
  }
}

const BASE_URLS = {
  __STASH_ENV__: 'https://login-edge.stash.com/',
  test: 'https://login-test.stash.com/',
  development: 'https://login-edge.stash.com/',
  edge: 'https://login-edge.stash.com/',
  staging: 'https://login-staging.stash.com/',
  production: 'https://login.stash.com/'
};
const CLIENT_IDS = {
  __STASH_ENV__: 'e0c11a43-b868-4a31-bb24-06a497b315b1',
  test: 'test-client-id',
  development: '188d93b5-e818-4577-ac41-ffcef3488c0c',
  edge: 'f29b429f-5483-42e5-8ba6-4aeb02d5d3a9',
  staging: 'e9c5b819-702a-4617-ac77-4c82723df5b7',
  production: '92652692-61cc-48f6-9d41-3031f3031edc'
};
const REDIRECT_URLS = {
  __STASH_ENV__: 'https://app-local.stash.com:57454/authorize',
  test: 'https://app-test.stash.com/authorize',
  development: 'https://app-local.stash.com:57456/authorize',
  edge: 'https://app-edge.stash.com/authorize',
  staging: 'https://app-staging.stash.com/authorize',
  production: 'https://app.stash.com/authorize'
};

const refreshAccessToken = function (refreshToken) {
  try {
    const env = window.Stash?.env || process.env.NODE_ENV;
    const root = BASE_URLS[env];
    const client_id = CLIENT_IDS[env];
    const redirect_uri = REDIRECT_URLS[env];
    const tokenConfig = {
      method: httpMethods.POST,
      headers: {
        Accept: 'application/json'
      },
      body: {
        refresh_token: refreshToken.replace('Bearer ', '')
      }
    };
    const tokenUrl = `${root}api/v1/token?grant_type=refresh_token&client_id=${client_id}&redirect_uri=${redirect_uri}`;
    return Promise.resolve(sendRequest(tokenUrl, tokenConfig)).then(function (newAuth) {
      if (!newAuth.ok) {
        Auth.logOut('refresh token failed');
      }

      return Promise.resolve(getJSON(newAuth)).then(function (payload) {
        Auth.set({
          accessToken: 'Bearer ' + payload.access_token,
          refreshToken: payload.refresh_token
        });
        return new Promise(resolve => resolve(payload));
      });
    });
  } catch (e) {
    return Promise.reject(e);
  }
};

const createFetchHandler = (url, config) => {
  const fetchHandler = function () {
    try {
      const authParams = Auth.get();
      const {
        accessToken,
        refreshToken
      } = authParams;
      return Promise.resolve(function () {
        if (checkExpired(accessToken)) {
          return function () {
            if (refreshToken) {
              return Promise.resolve(refreshAccessToken(refreshToken)).then(function (payload) {
                config.accessToken = 'Bearer ' + payload.access_token;
                return Promise.resolve(sendRequest(url, config)).then(function (response) {
                  return Promise.resolve(getJSON(response)).then(function (json) {
                    return Promise.resolve(handleSessionChange(json)).then(function () {
                      return json;
                    });
                  });
                });
              });
            } else {
              Auth.logOut('missing refresh token');
            }
          }();
        } else {
          return Promise.resolve(sendRequest(url, config)).then(function (response) {
            function _temp3() {
              return Promise.resolve(getJSON(response)).then(function (json) {
                return Promise.resolve(handleSessionChange(json)).then(function () {
                  return json;
                });
              });
            }

            const _temp2 = function () {
              if (response.status === 401) {
                const _temp = function () {
                  if (refreshToken) {
                    return Promise.resolve(refreshAccessToken(refreshToken)).then(function (payload) {
                      config.accessToken = 'Bearer ' + payload.access_token;
                      return Promise.resolve(sendRequest(url, config)).then(function (_sendRequest) {
                        response = _sendRequest;
                      });
                    });
                  } else {
                    Auth.logOut('missing refresh token');
                  }
                }();

                if (_temp && _temp.then) return _temp.then(function () {});
              }
            }();

            return _temp2 && _temp2.then ? _temp2.then(_temp3) : _temp3(_temp2);
          });
        }
      }());
    } catch (e) {
      return Promise.reject(e);
    }
  };

  return fetchHandler;
};

let customDedupingIntervals = {};
const defaultDedupingInterval = 2000;
const defaultSwrOptions = {
  shouldRetryOnError: false,
  revalidateOnFocus: false
};
const root = process.env.STASH_API_PATH || process.env.REACT_APP_STASH_API_PATH || '';
const defaults = {
  root,
  method: httpMethods.GET
};
function useCachedGet({
  onMount,
  createFetchHandler: createFetchHandler$1 = createFetchHandler,
  swrOptions = {},
  onSuccess,
  onError,
  ...userConfig
} = {}) {
  const [fetchArgs, setFetchArgs] = useState(false);
  const config = Object.assign({}, defaults, userConfig, fetchArgs || {});
  const shouldGet = Boolean(fetchArgs) || onMount;
  let initialArg = null;
  let fetchHandler;
  let dedupingInterval = defaultDedupingInterval;
  const url = buildURL(config, Auth.get());

  if (shouldGet) {
    initialArg = url;
    fetchHandler = createFetchHandler$1(url, config);

    if (config.path && customDedupingIntervals[config.path]) {
      dedupingInterval = customDedupingIntervals[config.path];
    }
  }

  const options = { ...defaultSwrOptions,
    dedupingInterval,
    ...swrOptions
  };
  if (onError) options.onError = onError;
  if (onSuccess) options.onSuccess = onSuccess;
  const {
    data,
    error,
    isValidating,
    revalidate,
    mutate
  } = useSWR(initialArg, fetchHandler, options);
  const fetch = useCallback(runtimeFetchArgs => {
    setFetchArgs(runtimeFetchArgs || {});
  }, [setFetchArgs]);
  return {
    fetch,
    data: data,
    error,
    loading: !shouldGet ? false : data === undefined && !error,
    isValidating,
    revalidate,
    mutate
  };
}
const setDedupingIntervals = intervals => {
  customDedupingIntervals = Object.assign({}, customDedupingIntervals, intervals);
};

const useDeepEqualCallback = (callback, deps) => {
  const callbackRef = useRef(callback);
  const cachedDeps = useRef(cloneDeep(deps));

  if (!isEqual(deps, cachedDeps.current)) {
    callbackRef.current = callback;
    cachedDeps.current = cloneDeep(deps);
  }

  return callbackRef.current;
};

function _catch$1(body, recover) {
  try {
    var result = body();
  } catch (e) {
    return recover(e);
  }

  if (result && result.then) {
    return result.then(void 0, recover);
  }

  return result;
}

const root$1 = process.env.STASH_API_PATH || process.env.REACT_APP_STASH_API_PATH || '';
const defaults$1 = {
  root: root$1,
  method: httpMethods.GET
};
const simpleFetch = function (config, createFetchHandler$1 = createFetchHandler) {
  try {
    const configWithDefaults = Object.assign({}, defaults$1, config);
    const url = buildURL(configWithDefaults, Auth.get());
    return Promise.resolve(createFetchHandler$1(url, configWithDefaults)());
  } catch (e) {
    return Promise.reject(e);
  }
}; // eslint-disable-next-line @typescript-eslint/no-explicit-any

function useFetch({
  createFetchHandler: createFetchHandler$1 = createFetchHandler,
  onSuccess,
  onError,
  throwOnError,
  ...userConfig
} = {}) {
  const [data, setData] = useState(undefined);
  const [error, setError] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const fetch = useDeepEqualCallback(function (fetchFuncConfig) {
    try {
      const config = { ...defaults$1,
        ...userConfig,
        ...fetchFuncConfig
      };
      const url = buildURL(config, Auth.get());
      const fetchHandler = createFetchHandler$1(url, config);
      setLoading(true);
      return Promise.resolve(_catch$1(function () {
        return Promise.resolve(fetchHandler()).then(function (data) {
          setData(data);
          setLoading(false);
          setError(undefined);
          if (onSuccess) onSuccess(data);
          return data;
        });
      }, function (e) {
        setError(e);
        setLoading(false);

        if (throwOnError) {
          throw e;
        } else if (onError) {
          onError(e);
        }
      }));
    } catch (e) {
      return Promise.reject(e);
    }
  }, [userConfig, createFetchHandler$1]);
  useEffect(() => {
    if (userConfig.onMount) fetch(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return {
    fetch,
    data,
    error,
    loading
  };
}
useFetch.POST = httpMethods.POST;
useFetch.PUT = httpMethods.PUT;
useFetch.PATCH = httpMethods.PATCH;
useFetch.GET = httpMethods.GET;
useFetch.DELETE = httpMethods.DELETE;
useFetch.HEAD = httpMethods.HEAD;
useFetch.OPTIONS = httpMethods.OPTIONS;

const deleteAuthData = Auth.deleteAll; // check there is in-memory auth data (but no guarantee it isn't stale)

const hasAuthData = () => {
  const authData = Auth.get();
  return Object.keys(stashDefaults).map(k => authData[k]).every(Boolean);
};

export { buildURL, deleteAuthData, getJSON, handleSessionChange, hasAuthData, mockFetch, sendRequest, setDedupingIntervals, simpleFetch, unmockFetch, useCachedGet, useFetch };
//# sourceMappingURL=index.es.js.map
